/**
 * BEST MISSIONS — the score table, in `localStorage`.
 *
 * Storage-agnostic on purpose, exactly like `career/career-store.ts`,
 * `vcr/tape-store.ts` and `audio/settings.ts`: `BestMissionsStore` is just
 * `getItem`/`setItem`, so the round trip is unit-testable against a plain
 * in-memory fake (`bun test` has no DOM). The ranking rules themselves —
 * what qualifies, how ties break, the 10-entry cap — live in
 * `@red-opus/engine`'s `best-missions.ts`; this module is only the shelf.
 */

import { insertBestMission, type BestMissionEntry, type BestMissionPlacement } from "@red-opus/engine";

export const BEST_MISSIONS_STORAGE_KEY = "red-opus/best-missions";

export interface BestMissionsStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function isEntry(value: unknown): value is BestMissionEntry {
  if (typeof value !== "object" || value === null) return false;
  const raw = value as Partial<Record<keyof BestMissionEntry, unknown>>;
  return (
    typeof raw.name === "string" &&
    typeof raw.missionTitle === "string" &&
    typeof raw.plane === "string" &&
    typeof raw.score === "number" &&
    typeof raw.recordedAt === "string"
  );
}

/** Every readable entry. A malformed one is dropped rather than taking the whole table down with it. */
export function loadBestMissions(store: BestMissionsStore): readonly BestMissionEntry[] {
  const raw = store.getItem(BEST_MISSIONS_STORAGE_KEY);
  if (raw === null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter(isEntry);
}

function writeAll(store: BestMissionsStore, entries: readonly BestMissionEntry[]): void {
  store.setItem(BEST_MISSIONS_STORAGE_KEY, JSON.stringify(entries));
}

/** Records a placed score and writes the re-ranked, re-capped table back. */
export function recordBestMission(store: BestMissionsStore, entry: BestMissionEntry): BestMissionPlacement {
  const placement = insertBestMission(loadBestMissions(store), entry);
  writeAll(store, placement.entries);
  return placement;
}

/** The browser's `localStorage`, or nothing when there isn't one (SSR, `bun test`). */
export function browserBestMissionsStore(): BestMissionsStore | undefined {
  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    return undefined;
  }
}
